import { Component, ElementRef, Injector, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';

// Configs
import { DISPERSION_METHOD_COMPONENTS_CONSTANT } from './configs';
import { ROUTES_CONSTANTS } from '@shared/configs';

// Models
import { DispersionMethodStrategyType } from './models';
import { PRODUCT_ID_ENUMERATION, ProductType } from '@core/model/enums';

// Strategy
import { dispersionMethodRegistryStrategy } from './strategy';

import {
  CHECK_PROCESS_STATE_ENUMERATION,
  checkProcessRedirect,
  CheckProcessResponseType,
  CheckProcessService,
} from '@core/service/check-process';

export interface DispersionMethodBuildOptionsType {
  viewName?: string;
  processId?: string;
  productId: string;
  email?: string;
  finalProcessResult: Function;
}
@Component({
  selector: 'app-dispersion-method',
  templateUrl: './dispersion-method.component.html',
  styleUrls: ['./dispersion-method.component.scss'],
})
export class DispersionMethodComponent implements OnInit, OnDestroy {
  @ViewChild('dispersionMethodRef', { static: false }) public dispersionMethodComponent: ElementRef;

  public isLoading: boolean;
  private readonly unSubscribe$: Subject<void>;
  private readonly webComponentService: WebcomponentsService;
  private readonly authService: AuthService;
  private readonly router: Router;
  private readonly checkProcessService: CheckProcessService;
  private readonly ngZone: NgZone;
  public selectedStrategy!: DispersionMethodStrategyType;

  constructor(private readonly injector: Injector) {
    this.webComponentService = this.injector.get(WebcomponentsService);
    this.authService = this.injector.get(AuthService);
    this.router = this.injector.get(Router);
    this.checkProcessService = this.injector.get(CheckProcessService);
    this.ngZone = this.injector.get(NgZone);

    this.isLoading = false;
    this.unSubscribe$ = new Subject<void>();
  }
  public ngOnInit(): void {
    this.isLoading = true;
    this.checkProcessService
      .checkProcess()
      .pipe(
        takeUntil(this.unSubscribe$),
        tap((checkProcessResponse: CheckProcessResponseType) => this.checkProcessResponseHandler(checkProcessResponse)),
        switchMap(() => this.webComponentService.isLoaded$.pipe(filter(Boolean), takeUntil(this.unSubscribe$)))
      )
      .subscribe({
        next: () => {
          this.renderDispersionMethodWebComponente();
        },
        error: (err) => {
          console.error('Error cargando estrategia o WebComponent', err);
          this.router.navigate([ROUTES_CONSTANTS.HOME]);
        },
      });
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private renderDispersionMethodWebComponente(): void {
    const dispersionMethod = this.dispersionMethodComponent.nativeElement;

    dispersionMethod.ngElementStrategy?.componentRef?.instance?.build({
      viewName: this.selectedStrategy.viewName ?? DISPERSION_METHOD_COMPONENTS_CONSTANT.VERIFICATION_SELECT_ACCOUNT,
      productId: this.selectedStrategy.productId,
      processId: this.selectedStrategy.processId,
      finalProcessResult: this.selectedStrategy.finalProcessFunc.bind(this),
      email: this.authService.user?.email,
    } as DispersionMethodBuildOptionsType);

    this.isLoading = false;
  }

  private checkProcessResponseHandler({ productType, state }: CheckProcessResponseType): void {
    this.ngZone.run(() => {
      const route = checkProcessRedirect(state as CHECK_PROCESS_STATE_ENUMERATION, [
        CHECK_PROCESS_STATE_ENUMERATION.PENDING_DISPERSION_METHOD,
      ]);

      if (route) {
        this.router.navigate([route]);
      }
    });

    const origin = productType === Number(PRODUCT_ID_ENUMERATION.RP) ? ProductType.RP : ProductType.RFYA;
    this.selectedStrategy = dispersionMethodRegistryStrategy[origin]?.({
      injector: this.injector,
    });
  }
}
